import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FrontMatterEditor } from "@/components/manuscript/ChapterManager";
import { ChapterNarrationEditor } from "@/components/manuscript/ChapterNarrationEditor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Upload,
  Download,
  BookOpen,
  Layers,
  History,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Pencil,
} from "lucide-react";

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-border bg-slate-950/40 px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg ${highlight ? "text-amber-300" : "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function ParagraphRuns({ runs, text }) {
  if (runs && runs.length) {
    return (
      <>
        {runs.map((r, i) => {
          let el = <span key={i}>{r.t}</span>;
          if (r.i) el = <em key={i}>{r.t}</em>;
          if (r.b) el = <strong key={i}>{r.i ? <em>{r.t}</em> : r.t}</strong>;
          return el;
        })}
      </>
    );
  }
  return text;
}

function SceneDivider({ label }) {
  return (
    <div className="my-8 flex items-center justify-center gap-3 text-amber-500/50" data-testid="scene-divider">
      <span className="h-px w-16 bg-amber-500/20" />
      <span className="font-mono text-xs tracking-[0.3em]">{label || "◆ ◆ ◆"}</span>
      <span className="h-px w-16 bg-amber-500/20" />
    </div>
  );
}

function ChangeReport({ report }) {
  if (!report) return null;
  const clean = report.no_changes;
  return (
    <div className="space-y-3">
      <div
        className={`flex items-center gap-2 rounded-lg px-4 py-3 ${
          clean ? "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30" : "bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/30"
        }`}
      >
        {clean ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {clean
          ? "Round-trip clean — no spoken paragraphs added, deleted, or modified."
          : "Changes detected between versions."}
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Unchanged" value={report.unchanged} />
        <Stat label="Added" value={report.added} />
        <Stat label="Deleted" value={report.deleted} />
        <Stat label="Modified" value={report.modified} />
      </div>
    </div>
  );
}

export function ManuscriptView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusOrder = searchParams.get("focus") ? parseInt(searchParams.get("focus"), 10) : null;
  const fileRef = useRef(null);
  const [project, setProject] = useState(null);
  const [version, setVersion] = useState(undefined); // undefined=loading, null=none
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [paragraphs, setParagraphs] = useState([]);
  const [loadingParas, setLoadingParas] = useState(false);
  const [importing, setImporting] = useState(false);
  const [versions, setVersions] = useState([]);
  const [changeReport, setChangeReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [editing, setEditing] = useState(false);

  const loadActive = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/manuscript`);
    const active = data.active;
    setVersion(active);
    try {
      const { data: cd } = await api.get(`/projects/${projectId}/chapters`);
      setChapters(cd.chapters);
      setSelectedChapter(cd.chapters[0]?.id ?? null);
    } catch {
      if (active) { setChapters(active.chapters); setSelectedChapter(active.chapters[0]?.order ?? 1); }
    }
  }, [projectId]);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    loadActive().catch(() => setVersion(null));
  }, [projectId, loadActive]);

  useEffect(() => {
    if (selectedChapter == null) return;
    setEditing(false);
    setLoadingParas(true);
    api
      .get(`/projects/${projectId}/chapters/${selectedChapter}/paragraphs`)
      .then((r) => setParagraphs(r.data.paragraphs || []))
      .catch(() => setParagraphs([]))
      .finally(() => setLoadingParas(false));
  }, [projectId, selectedChapter]);

  useEffect(() => {
    // Search focus: chapter-local reader; deep-jump by global order is disabled with
    // first-class chapters. (Scroll-to-paragraph still applies within the open chapter.)
  }, [version, focusOrder]);

  useEffect(() => {
    if (focusOrder == null || loadingParas) return;
    const el = document.querySelector(`[data-testid="paragraph-${focusOrder}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [paragraphs, focusOrder, loadingParas]);

  const doImport = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error("Please choose a .docx file.");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/manuscript`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported v${data.version.version_number}: ${data.version.word_count.toLocaleString()} words, ${data.version.paragraph_count} paragraphs.`);
      setVersion(data.version);
      await loadActive();
      if (data.change_report) {
        setChangeReport(data.change_report);
        setShowReport(true);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openVersions = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/manuscript/versions`);
      setVersions(data);
      setShowVersions(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const compareTo = async (otherId) => {
    try {
      const { data } = await api.get(`/manuscript/versions/${version.id}/compare?to=${otherId}`);
      setChangeReport(data);
      setShowVersions(false);
      setShowReport(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadOriginal = async () => {
    try {
      const res = await api.get(`/manuscript/versions/${version.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = version.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download original file.");
    }
  };

  const currentChapter = chapters.find((c) => c.id === selectedChapter);

  return (
    <div>
      <Button
        variant="ghost"
        onClick={() => navigate(`/studio/projects/${projectId}`)}
        data-testid="back-to-project"
        className="mb-4 gap-2 text-slate-400 hover:text-amber-200"
      >
        <ArrowLeft className="h-4 w-4" /> {project?.title || "Project"}
      </Button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 2 · Manuscript</div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Manuscript</h1>
        </div>
        {version && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".docx"
              data-testid="manuscript-file-input-header"
              className="hidden"
              onChange={(e) => doImport(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              data-testid="manage-chapters-button"
              onClick={() => navigate(`/studio/projects/${projectId}/chapters`)}
              className="gap-2 border-border text-slate-300 hover:bg-slate-800/50"
            >
              <Layers className="h-4 w-4" /> Manage chapters
            </Button>
            <Button
              variant="outline"
              data-testid="versions-button"
              onClick={openVersions}
              className="gap-2 border-border text-slate-300 hover:bg-slate-800/50"
            >
              <History className="h-4 w-4" /> Versions
            </Button>
            <Button
              variant="outline"
              data-testid="download-original-button"
              onClick={downloadOriginal}
              disabled={!version.has_file}
              className="gap-2 border-border text-slate-300 hover:bg-slate-800/50"
            >
              <Download className="h-4 w-4" /> Original
            </Button>
            <Button
              data-testid="import-new-version-button"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Import new version
            </Button>
          </div>
        )}
      </div>

      {version === undefined ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : version === null ? (
        <ImportPanel importing={importing} onImport={doImport} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
            <Stat label="Spoken words" value={version.word_count.toLocaleString()} highlight />
            <Stat label="Body paragraphs" value={version.paragraph_count} highlight />
            <Stat label="Chapters" value={chapters.length || version.chapter_count} />
            <Stat label="Characters" value={version.char_count.toLocaleString()} />
            <Stat label="Version" value={`v${version.version_number}`} />
          </div>

          <FrontMatterEditor projectId={projectId} onSaved={loadActive} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            {/* Chapter tree */}
            <div className="rounded-xl border border-border bg-[#12161f] p-3">
              <div className="mb-2 flex items-center gap-2 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <Layers className="h-3.5 w-3.5" /> Chapters
              </div>
              {chapters.map((c) => (
                <button
                  key={c.id}
                  data-testid={`chapter-tab-${c.id}`}
                  onClick={() => setSelectedChapter(c.id)}
                  className={`mb-1 w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    selectedChapter === c.id
                      ? "bg-amber-500/10 text-amber-200 ring-1 ring-amber-500/20"
                      : "text-slate-400 hover:bg-amber-500/5 hover:text-amber-100"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{c.number_label}</span>
                    {c.kind && c.kind !== "chapter" && (
                      <span className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-violet-300">{c.kind}</span>
                    )}
                  </div>
                  <div className="font-serif text-base leading-tight">{c.display_title || c.title}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-slate-500">
                    {(c.word_count ?? 0).toLocaleString()} w · {c.paragraph_count ?? 0} p · {c.scene_count ?? 1} sc
                  </div>
                </button>
              ))}
            </div>

            {/* Reader */}
            <div className="rounded-xl border border-border bg-[#0f1217] p-8" data-testid="manuscript-reader">
              {editing && currentChapter ? (
                <ChapterNarrationEditor
                  projectId={projectId}
                  chapterId={currentChapter.id}
                  chapterTitle={currentChapter.display_title || currentChapter.title}
                  onClose={() => setEditing(false)}
                  onCommitted={async () => {
                    await loadActive();
                    const r = await api.get(`/projects/${projectId}/chapters/${currentChapter.id}/paragraphs`);
                    setParagraphs(r.data.paragraphs || []);
                  }}
                />
              ) : loadingParas ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                </div>
              ) : (
                <motion.div
                  key={selectedChapter}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="mx-auto max-w-2xl"
                >
                  {currentChapter && (
                    <div className="mb-8 flex items-center justify-between gap-3">
                      <h2 className="flex-1 text-center font-serif text-3xl text-slate-100">{currentChapter.display_title || currentChapter.title}</h2>
                      <Button
                        size="sm"
                        data-testid="edit-narration-button"
                        onClick={() => setEditing(true)}
                        className="shrink-0 gap-1.5 bg-amber-500 text-slate-950 hover:bg-amber-400"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit narration
                      </Button>
                    </div>
                  )}
                  {paragraphs.map((p) => {
                    if (p.kind === "heading") return null; // shown as chapter title
                    if (p.kind === "blank") return <SceneDivider key={p.order} />;
                    if (p.kind === "scene_marker") return <SceneDivider key={p.order} label={p.text.trim()} />;
                    return (
                      <p
                        key={p.order}
                        data-testid={`paragraph-${p.order}`}
                        className={`mb-4 font-serif text-[17px] leading-[1.85] text-slate-300 ${p.order === focusOrder ? "-mx-3 rounded bg-amber-500/10 px-3 py-1 ring-1 ring-amber-500/40" : ""}`}
                      >
                        <ParagraphRuns runs={p.runs} text={p.text} />
                      </p>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Change report dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Change report</DialogTitle>
            <DialogDescription className="text-slate-400">
              Paragraph-level comparison of spoken text between versions.
            </DialogDescription>
          </DialogHeader>
          <ChangeReport report={changeReport} />
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="border-border bg-[#12161f] text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Manuscript versions</DialogTitle>
            <DialogDescription className="text-slate-400">
              Every import is preserved. Compare any earlier version against the active one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {versions.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between rounded-lg border border-border bg-slate-950/40 px-4 py-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-slate-200">v{v.version_number}</span>
                    {v.active && (
                      <Badge className="rounded-full border-0 bg-emerald-500/15 px-2 py-0 text-[9px] uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30">
                        Active
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-500">
                    {v.word_count.toLocaleString()} w · {v.paragraph_count} p · {new Date(v.imported_at).toLocaleString()}
                  </div>
                </div>
                {!v.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`compare-version-${v.version_number}`}
                    onClick={() => compareTo(v.id)}
                    className="gap-1 border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
                  >
                    Compare
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportPanel({ importing, onImport }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        onImport(e.dataTransfer.files?.[0]);
      }}
      className={`mt-6 flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-24 text-center transition-colors ${
        drag ? "border-amber-500/60 bg-amber-500/[0.06]" : "border-border bg-[#12161f]/40"
      }`}
    >
      <input
        ref={ref}
        type="file"
        accept=".docx"
        data-testid="manuscript-file-input"
        className="hidden"
        onChange={(e) => onImport(e.target.files?.[0])}
      />
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/25">
        {importing ? <Loader2 className="h-7 w-7 animate-spin text-amber-400" /> : <BookOpen className="h-7 w-7 text-amber-400" />}
      </div>
      <h2 className="font-serif text-2xl text-slate-200">Import the manuscript</h2>
      <p className="mt-2 max-w-sm text-sm text-slate-500">
        Drop a <span className="font-mono text-slate-400">.docx</span> file here, or browse. The original is preserved
        immutably; chapters, scenes, paragraphs and word counts are detected automatically.
      </p>
      <Button
        onClick={() => ref.current?.click()}
        disabled={importing}
        data-testid="browse-file-button"
        className="mt-6 gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
      >
        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {importing ? "Importing…" : "Choose DOCX file"}
      </Button>
    </div>
  );
}
